import { useState } from 'react';
import type { LibraryDefinitionAdminResponse } from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Skill/Qualification/Experience-Area Library Administration (post-MVP
 * increment, 2026-09-21) — closing the gap `docs/DOMAIN_DECISIONS.md`
 * "Blocking Decision Batch" PD-10 named explicitly: these three libraries
 * had a listing endpoint and no create/edit/retire admin action.
 *
 * One component, not three near-identical copies of `AttributeLibrarySection`
 * — the same reasoning the backend's generic `LibraryAdminOps` already
 * applies. These three tables carry no "meaning" field, so none of
 * `AttributeLibrarySection`'s scale-wording-lock or cosmetic-attestation
 * machinery applies here: `name` and `category` are two plain labels,
 * freely editable at any time, with retirement (optionally naming a
 * replacement) the only other lifecycle transition.
 */
interface SimpleLibraryConfig {
  title: string;
  note: string;
  kind: string;
  listPath: string;
  responseKey: 'skills' | 'qualifications' | 'experienceAreas';
  newLabel: string;
}

interface ListResponse {
  count: number;
  skills?: LibraryDefinitionAdminResponse[];
  qualifications?: LibraryDefinitionAdminResponse[];
  experienceAreas?: LibraryDefinitionAdminResponse[];
}

export function SimpleLibrarySection(config: SimpleLibraryConfig) {
  const library = useApiResource<ListResponse>(config.listPath);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label={config.title}>
      <h2 className={styles.sectionTitle}>{config.title}</h2>
      <p className={styles.sectionNote}>{config.note}</p>

      {library.status === 'loading' ? <LoadingState label={`Loading the ${config.kind} library…`} /> : null}
      {library.status === 'failed' ? (
        <ErrorState title={`The ${config.kind} library could not be loaded`} detail={describeError(library.error)} />
      ) : null}

      {library.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {(library.data[config.responseKey] ?? []).map((entry) => (
              <li key={entry.id} className={styles.row}>
                <span className={styles.rowName}>{entry.name}</span>
                <StatusTag status={entry.status} />
                <span className={styles.rowMeta}>{entry.category ?? 'Uncategorised'}</span>
                <span className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setEditing(editing === entry.id ? null : entry.id)}
                  >
                    {editing === entry.id ? 'Close' : 'Edit'}
                  </button>
                </span>
                {editing === entry.id ? (
                  <SimpleLibraryEditor
                    config={config}
                    entry={entry}
                    onDone={() => {
                      setEditing(null);
                      library.reload();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>

          {creating ? (
            <SimpleLibraryCreator
              config={config}
              onDone={() => {
                setCreating(false);
                library.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                {config.newLabel}
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function SimpleLibraryCreator({ config, onDone }: { config: SimpleLibraryConfig; onDone: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost(config.listPath, { name, category: category || null });
      return `"${name}" was added to the library.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title={config.newLabel} onSubmit={submit} submitLabel="Create" busy={state.status === 'saving'}>
      <Field id={`new-${config.kind}-name`} label="Name" value={name} onChange={setName} />
      <Field id={`new-${config.kind}-category`} label="Category" value={category} onChange={setCategory} hint="Optional." />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function SimpleLibraryEditor({
  config,
  entry,
  onDone,
}: {
  config: SimpleLibraryConfig;
  entry: LibraryDefinitionAdminResponse;
  onDone: () => void;
}) {
  const [name, setName] = useState(entry.name);
  const [category, setCategory] = useState(entry.category ?? '');
  const { state, run } = useAction();

  async function save() {
    const ok = await run(async () => {
      await apiPatch(`${config.listPath}/${entry.id}`, { name, category: category || null });
      return `"${name}" was updated.`;
    });
    if (ok) onDone();
  }

  async function deprecate() {
    const ok = await run(async () => {
      await apiPost(`${config.listPath}/${entry.id}/deprecation`, {});
      return `"${entry.name}" was retired from the library.`;
    });
    if (ok) onDone();
  }

  async function reactivate() {
    const ok = await run(async () => {
      await apiPost(`${config.listPath}/${entry.id}/reactivation`, {});
      return `"${entry.name}" was reactivated.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title={`Edit ${entry.name}`} onSubmit={save} submitLabel="Save changes" busy={state.status === 'saving'}>
      <Field id={`name-${entry.id}`} label="Name" value={name} onChange={setName} />
      <Field id={`category-${entry.id}`} label="Category" value={category} onChange={setCategory} hint="Optional." />

      <ActionFeedback state={state} />

      {entry.status === 'active' ? (
        <ConfirmAction
          label={`Retire this ${config.kind}`}
          confirmLabel="Retire it"
          busy={state.status === 'saving'}
          description={
            <>
              Retiring <strong>{entry.name}</strong> removes it from the list a role requirement or a person's record
              can newly reference. It is <strong>not</strong> deleted: every existing record naming it stays exactly
              as it is.
            </>
          }
          onConfirm={deprecate}
        />
      ) : (
        <>
          <p className={styles.locked}>
            This {config.kind} is retired. Records that already reference it are unchanged.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={reactivate} disabled={state.status === 'saving'}>
              {state.status === 'saving' ? 'Working…' : `Reactivate this ${config.kind}`}
            </button>
          </div>
        </>
      )}
    </AdminForm>
  );
}

export const SKILL_LIBRARY_CONFIG: SimpleLibraryConfig = {
  title: 'Skill library',
  note: 'The skills people can record against their profile and roles can require. Retired skills stay listed here and keep the records recorded against them.',
  kind: 'skill',
  listPath: '/admin/skills',
  responseKey: 'skills',
  newLabel: 'Define a new skill',
};

export const QUALIFICATION_LIBRARY_CONFIG: SimpleLibraryConfig = {
  title: 'Qualification library',
  note: 'The qualifications people can record against their profile and roles can require. Retired qualifications stay listed here and keep the records recorded against them.',
  kind: 'qualification',
  listPath: '/admin/qualifications',
  responseKey: 'qualifications',
  newLabel: 'Define a new qualification',
};

export const EXPERIENCE_AREA_LIBRARY_CONFIG: SimpleLibraryConfig = {
  title: 'Experience area library',
  note: 'The experience areas people can record against their profile and roles can require. Retired experience areas stay listed here and keep the records recorded against them.',
  kind: 'experience area',
  listPath: '/admin/experience-areas',
  responseKey: 'experienceAreas',
  newLabel: 'Define a new experience area',
};
